import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Paper, Box, Grid, Typography, Button, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { grey } from '@mui/material/colors';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { MessageModal } from 'ui/Modals';
import { exportToExcel } from 'services';
import { formatDate, formatCurrency } from 'shared/functions';
import { useQuery } from 'shared/hooks';
import { addNotificationAction } from 'store/notifications/notificationsActions';

import { getShopProductValues } from '../shared/functions/getShopProductValues/getShopProductValues';
import { removeShopProductsFromBasketAction, setShopActiveViewAction } from '../store/shopActions';
import { sendMessageToSales } from '../services';
import { getProductsFromBasket } from '../selectors/shopSelectors';
import { ShopSelectionList } from './ShopSelectionList/ShopSelectionList';
import { ShopCheckoutForm } from './ShopCheckoutForm';

const useStyle = makeStyles((theme) => ({
  title: {
    display: 'flex',
    alignItems: 'center',
  },
  titleText: {
    position: 'relative',
    top: 4,
    fontSize: '1.5rem',
  },
  value: {
    color: theme.palette.primary.main,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  summary: {
    borderStyle: 'solid',
    borderWidth: '0 0 5px 0',
    borderColor: grey[200],
  },
  total: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  button: {
    width: '100%',
    minWidth: 120,
  },
  stepButton: {
    minWidth: 120,
  },
  finishIcon: {
    width: 150,
    height: 150,
    color: theme.palette.primary.main,
  },
  message: {
    textAlign: 'center',
  },
}));

const ShopBasket = ({ activeState, urlsState }) => {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tab } = useQuery();

  const { lastUpdate } = useSelector(({ shop }) => shop.history.basket);
  const { data: products } = useSelector(getProductsFromBasket);

  const [urls, setUrls] = urlsState;
  const [activeStep, setActiveStep] = activeState;

  const [summaryPrice, setSummaryPrice] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const toggleMessageModal = () => setMessageModalOpen(!messageModalOpen);

  const handleSelectionChange = (items) => {
    setSelectedProducts(items);
  };

  const actions = [
    {
      name: t('Delete'),
      action: () => {
        if (!selectedProducts.length) return;
        dispatch(removeShopProductsFromBasketAction({ ids: selectedProducts }));
      },
    },
    {
      name: t('Export to Excel'),
      action: () => {
        if (!selectedProducts.length) return;

        const filteredProducts = products
          .filter(({ id }) => selectedProducts.includes(id))
          .map((product) =>
            getShopProductValues(product, ['name', 'validity', 'category', 'price']),
          );

        exportToExcel({
          fileName: `ShopBasket`,
          workbookName: t('My basket'),
          columns: [t('Name'), t('Validity'), t('Category'), t('Price')],
          data: filteredProducts,
        });
      },
    },
  ];

  useEffect(() => {
    setSummaryPrice(
      products.reduce((sum, { pricing }) => {
        const item = pricing.find(({ active }) => active);
        const price = item ? item.price : 0;

        return sum + price;
      }, 0),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useEffect(() => {
    dispatch(setShopActiveViewAction({ view: tab || null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleOnSuccess = (data) => {
    setUrls(data);

    handleNext();
  };

  const backToProductList = () => {
    navigate({
      search: ``,
    });
  };

  const redirectToDashboard = () => {
    window.location.href = `${window.location.origin}/page-one`;
  };

  const sendMessage = (messageData) => {
    sendMessageToSales(messageData)
      .then(() => {
        dispatch(
          addNotificationAction({
            message: t('Message sent successfully'),
            variant: 'success',
          }),
        );
      })
      .catch(() => {
        dispatch(
          addNotificationAction({
            message: t('Failed to send a message'),
            variant: 'error',
          }),
        );
      });
  };

  return (
    <>
      {activeStep === 0 && (
        <>
          <Box mb={1}>
            <IconButton onClick={backToProductList} size="large">
              <ArrowBackIcon />
            </IconButton>
            <span className={classes.titleText}>{t('My basket')}</span>
          </Box>
          <Paper elevation={3}>
            <Box pl={7} pr={9} py={6} className={classes.summary}>
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Box>
                    <Typography variant="h5">{t('Total')}</Typography>
                    <Typography variant="h4" className={classes.value}>
                      {products.length} {t('packages')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <Box>
                    <Typography variant="h5">{t('Amount')}</Typography>
                    <Typography variant="h4" className={classes.value}>
                      {formatCurrency(summaryPrice, 'EUR')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <Box>
                    <Typography variant="h5">{t('Last update')}</Typography>
                    <Typography variant="h4" className={classes.value}>
                      {lastUpdate ? formatDate(lastUpdate) : '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <Grid container direction="column" spacing={2}>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={handleNext}
                        disabled={!products.length}
                      >
                        {t('Checkout')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="outlined"
                        color="primary"
                        className={classes.button}
                        onClick={toggleMessageModal}
                      >
                        {t('Contact sales')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <ShopSelectionList
                actions={actions}
                products={products}
                onSelectionChange={handleSelectionChange}
                columns={['name', 'validity', 'category', 'pricing']}
              />
              <Box pl={2} pr={15} py={2} className={classes.total}>
                <Grid container justifyContent="space-between">
                  <Grid item>
                    <Typography variant="h5">{t('Total')}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="h5">{formatCurrency(summaryPrice, 'EUR')}</Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Paper>
          <MessageModal
            title={t('Contact Sales')}
            open={messageModalOpen}
            onClose={toggleMessageModal}
            onSendMessage={sendMessage}
          />
        </>
      )}
      {activeStep === 1 && (
        <Paper elevation={3}>
          <ShopCheckoutForm products={products} onSuccess={handleOnSuccess}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.stepButton}
              onClick={handleBack}
            >
              {t('Back')}
            </Button>
          </ShopCheckoutForm>
        </Paper>
      )}
      {activeStep === 2 && (
        <Paper elevation={3}>
          <Box p={6}>
            <Grid container justifyContent="center" alignItems="center" direction="column">
              <Grid item>
                <CheckCircleOutlineIcon className={classes.finishIcon} />
              </Grid>
              <Grid item>
                <Typography variant="h5" className={classes.message}>
                  {t('Invoice successfully created!')}
                </Typography>
                <Typography variant="h6" className={classes.message}>
                  {t(
                    'You can pay using one of the payment methods given in the invoice or using bank transfer given in the invoice.',
                  )}
                </Typography>
              </Grid>
              <Grid item>
                <Box pt={3}>
                  <Grid container spacing={2}>
                    <Grid item>
                      <Button
                        component="a"
                        href={urls.invoiceStripePaymentUrl}
                        variant="contained"
                        color="primary"
                        target="_blank"
                      >
                        {t('Pay online')}
                      </Button>
                    </Grid>

                    <Grid item>
                      <Button
                        component="a"
                        href={urls.invoicePdfUrl}
                        variant="contained"
                        color="primary"
                        target="_blank"
                      >
                        {t('Download invoice')}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box p={2}>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.stepButton}
                  onClick={redirectToDashboard}
                >
                  {t('Finish')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}
    </>
  );
};

ShopBasket.propTypes = {
  activeState: PropTypes.array.isRequired,
  urlsState: PropTypes.array.isRequired,
};

export { ShopBasket };
